/**
 * 사진 업로드 위젯 한 벌 — 트리 photo 위젯 · DataDefField photo 셀 · 행 그리드 photo 필드가
 * **같은 컴포넌트/흐름**을 쓴다(사본 금지).
 *
 * composerWidgetTreeRender에서 분리(2026-09-07 — 행 그리드 photo 위젯 도입): RowsCollectionEditor가
 * 이 위젯을 쓰는데 composerWidgetTreeRender는 RowsCollectionEditor를 import하므로(스타일·필드
 * 컴포넌트 승계) 그쪽에 두면 순환이 된다. 기존 공개 이름(runPhotoUpload·takeSelectedFile·
 * PHOTO_UPLOAD_ERROR_TEXT·ComposerPhotoUpload)은 composerWidgetTreeRender가 re-export로 유지한다.
 */
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { READONLY_IMAGE_STYLE } from './AnnotatedImageView';
import { ImageWithFallback } from './ImageWithFallback';
import { addItemButtonStyle } from './addItemButton';

/**
 * photo 위젯 업로드 호스트 콜백 — 파일을 업로드하고 저장할 URL을 반환한다(rc는 API 직접 호출
 * 안 함 — 패키지 순수성, registered 위젯 주입과 같은 축). 미배선 호스트(external 무인증 등)에선
 * 안내 placeholder로 degrade.
 */
export type ComposerPhotoUpload = (file: File) => Promise<string>;

/** 업로드 실패 인라인 문구 — spec이 "실패 시 값 불변"과 함께 못 박는 계약 텍스트. */
export const PHOTO_UPLOAD_ERROR_TEXT = '사진 업로드에 실패했습니다. 다시 시도해 주세요.';

/** 표 셀 photo 컴팩트 썸네일 결(96×64) — 편집 셀(compact)·readonly 셀이 한 벌 공유(사본 금지). */
export const PHOTO_CELL_THUMB_STYLE: CSSProperties = {
  width: 96,
  height: 64,
  objectFit: 'cover',
  background: '#f9fafb',
  display: 'block',
};

/**
 * photo 업로드 비동기 흐름 정본 — 파일·콜백·진행 중 게이트 → 업로드 → 커밋/실패 문구.
 * PhotoUploadWidget은 여기에 상태 setter와 alive 판정만 배선한다. rc엔 jsdom이 없어
 * (photoWidget.test 규약) 비동기 계약(진행 표시·실패 시 값 불변·성공 커밋 인자·언마운트 stale
 * 커밋 차단)은 이 함수를 deferred promise로 단위 검증하고, 마크업 계약은 정적 렌더 spec이 못 박는다.
 * isAlive: await 사이 언마운트(행 삭제·화면 이탈)면 커밋·상태쓰기를 전부 버린다 — 언마운트 후
 * 커밋하면 그 자리(인덱스)에 온 **다른 행**에 URL이 박힐 수 있다.
 */
export async function runPhotoUpload({
  file,
  uploadPhoto,
  uploading,
  isAlive,
  setUploading,
  setError,
  commit,
}: {
  file: File | undefined;
  uploadPhoto: ComposerPhotoUpload | undefined;
  uploading: boolean;
  isAlive: () => boolean;
  setUploading: (next: boolean) => void;
  setError: (next: string | null) => void;
  commit: (url: string) => void;
}): Promise<void> {
  if (!file || !uploadPhoto || uploading) return;
  setUploading(true);
  setError(null);
  try {
    const nextUrl = await uploadPhoto(file);
    if (isAlive()) commit(nextUrl);
  } catch {
    if (isAlive()) setError(PHOTO_UPLOAD_ERROR_TEXT);
  } finally {
    if (isAlive()) setUploading(false);
  }
}

/**
 * 파일 입력에서 선택 파일을 꺼내고 입력값을 비운다 — 같은 파일 재선택(실패 후 재시도)도 change가
 * 다시 발화하는 규약의 정본(입력값을 비우지 않으면 브라우저가 같은 파일 재선택을 무시한다).
 */
export function takeSelectedFile(target: {
  files: ArrayLike<File> | null;
  value: string;
}): File | undefined {
  const file = target.files?.[0];
  target.value = '';
  return file;
}

/**
 * 사진 업로드 위젯(widget:'photo', 편집 모드) — 값=string(업로드된 사진 URL), 빈 값=''.
 * 파일 선택(accept image/* — 모바일에선 카메라/갤러리 겸용) → uploadPhoto(호스트 주입)로 업로드 →
 * 반환 URL을 onValueChange로 저장 → <img> 미리보기 + 삭제(값 ''). 실패는 인라인 문구(값 불변).
 * 업로드 중 버튼 비활성 + 문구. 캔버스(authoringCanvas)는 실폼과 같은 버튼 모양을 유지하되
 * 동작만 inert(suggest 캔버스 규약 동형 — 모양이 갈라지면 WYSIWYG가 깨진다).
 * 미리보기 시각(높이 112·cover·바탕 #f9fafb)은 image 위젯 동형(인라인 소유 — rc Tailwind purge 규약).
 *
 * compact: 표 셀(DataDefField widget:'photo')·행 그리드 필드 배치용 컴팩트 결 — 썸네일을 셀
 * 밀도에 맞춰 줄이고(96×64) 버튼도 작게(패딩·폰트). 값 규약·업로드 흐름은 동일(같은 컴포넌트
 * 재사용 — 독립 배치판과 셀판이 갈라지지 않게 한다).
 */
export function PhotoUploadWidget({
  value,
  alt,
  onValueChange,
  labelEl,
  uploadPhoto,
  disabled,
  authoringCanvas,
  compact,
}: {
  value: unknown;
  /** <img alt> — 독립 배치는 node.label, 표 셀은 칼럼 라벨. */
  alt: string;
  onValueChange: (next: string) => void;
  /** 라벨 요소(독립 배치 경로) — 표 셀은 헤더가 라벨이라 미전달. */
  labelEl?: ReactNode;
  uploadPhoto?: ComposerPhotoUpload;
  disabled?: boolean;
  authoringCanvas?: boolean;
  compact?: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // 업로드 await 사이 언마운트(행 삭제·화면 이탈) 감지 — stale 커밋/상태쓰기 차단(runPhotoUpload isAlive).
  const aliveRef = useRef(true);
  useEffect(() => {
    // StrictMode 재마운트: cleanup이 false로 내린 뒤 effect 본문이 다시 돌므로 여기서 재점등.
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  const url = typeof value === 'string' ? value.trim() : '';
  const inert = authoringCanvas === true || disabled === true;

  const handleFile = (file: File | undefined): Promise<void> =>
    runPhotoUpload({
      file,
      uploadPhoto,
      uploading,
      isAlive: () => aliveRef.current,
      setUploading,
      setError,
      commit: onValueChange,
    });

  // 썸네일 — 독립 배치=폭 채움(image 위젯 동형 — READONLY_IMAGE_STYLE 공유),
  // 셀=컴팩트 고정(PHOTO_CELL_THUMB_STYLE — readonly 셀과 한 벌, 셀 밀도 침범 방지).
  const thumbStyle: CSSProperties = compact ? PHOTO_CELL_THUMB_STYLE : READONLY_IMAGE_STYLE;
  const buttonStyle: CSSProperties = {
    ...addItemButtonStyle,
    ...(compact ? { padding: '4px 10px', fontSize: 12 } : {}),
    // 시각 흐림은 실폼에서도 흐려지는 상태(업로드 중·disabled)만 — 캔버스(authoringCanvas)는
    // 동작만 inert고 모양은 실폼과 같아야 한다(WYSIWYG, 위 JSDoc). inert를 합치면 캔버스가 흐려진다.
    ...(uploading || disabled === true ? { opacity: 0.6, cursor: 'default' } : {}),
  };

  return (
    <div>
      {labelEl}
      {url ? (
        <div>
          {/* 로드 실패도 같은 발자국의 안내 칸으로 접는다(ImageWithFallback 헤더 — P6-d). 편집
              경로에서도 폴백이 낫다 — 깨진 아이콘은 「업로드가 실패했나」로 읽히는데, 실제 상태는
              「저장된 URL이 더 이상 없다」이고 그 판단이 곧 아래 「사진 삭제」·재업로드로 이어진다. */}
          <ImageWithFallback src={url} alt={alt} style={thumbStyle} />
          <button
            type="button"
            className="mt-1 text-xs text-red-600 hover:underline"
            disabled={inert}
            onClick={() => {
              if (inert) return;
              setError(null);
              onValueChange('');
            }}
          >
            사진 삭제
          </button>
        </div>
      ) : (
        <div>
          {/* 파일 입력은 숨기고 버튼이 대리 클릭 — 버튼 모양은 "+ 항목 추가" 승계(addItemButtonStyle). */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              // 같은 파일 재선택(실패 후 재시도) 규약 — takeSelectedFile이 입력값 비움까지 소유.
              void handleFile(takeSelectedFile(e.target));
            }}
          />
          <button
            type="button"
            style={buttonStyle}
            disabled={uploading || disabled}
            onClick={() => {
              if (inert || uploading) return;
              fileInputRef.current?.click();
            }}
          >
            {uploading ? '업로드 중…' : '+ 사진 선택'}
          </button>
        </div>
      )}
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </div>
  );
}
